import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";
import { ISSUE_OPERATOR_ACTION } from "../../issue-operator-action";

/**
 * The dump objects stopped arriving.
 *
 * Raised from the BUCKET, never from a report: the producer's calls are
 * asynchronous, so a green submission and a job that died afterwards look
 * identical from the control plane's side. Only the landed object can tell
 * them apart, which is why the sweep lists S3 rather than reading the ledger
 * it also has to hand.
 *
 * Also covers an object that arrived but is implausibly small. A truncated
 * archive is the failure a job-status check cannot see at all, and a
 * zero-length one would otherwise satisfy every "is there a recent dump"
 * question ever asked of it.
 */
export const VIBE_APP_BACKUP_STALE = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message:
    "No recent logical dump has landed in the tenant's app-backup bucket for this app, or the newest one is too small to be a real archive. The daily pg_dump either stopped running or stopped uploading. Raised per app from a listing of the bucket itself rather than from the agent's report, because an asynchronous job can report success and then fail. Cleared automatically once a fresh object of a plausible size appears.",
  severity: "critical",
  remedy:
    "Run the dump now. This does not call AWS Backup — the app family is a pg_dump pipeline that runs on the tenant's own Nomad, which the control plane cannot reach — so it records a request the tenant's agent picks up on its next poll (seconds, not minutes) and then re-runs today's dump from scratch. The request applies to today only and lapses at midnight UTC. If nothing happens at all, the cause is upstream of the dump: either the app has no managed data bound, or the tenant's stacks have not fully applied yet (the app-backup bucket and CMK, and Aurora's coordinates, are all required and converge independently), and the button refuses with which of the two it is. If the dump runs and fails, VIBE_APP_BACKUP_JOB_FAILING carries the reason.",
  operatorAction: ISSUE_OPERATOR_ACTION.VIBE_RUN_APP_BACKUP_NOW
} as const satisfies IssueDefinition;
